import { useQuery } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import type { Database } from '../types/database.types';

type Project = Database['public']['Tables']['projects']['Row'];
type Employee = Database['public']['Tables']['employees']['Row'];
type Activity = Database['public']['Tables']['activities']['Row'];
type CostCategory = Database['public']['Tables']['cost_categories']['Row'];

type TimeEntry = Database['public']['Tables']['time_entries']['Row'] & {
  projects?: { name: string; hourly_rate: number | null } | null;
  employees?: { 
    full_name: string; 
    hourly_rate: number | null;
  } | null;
  activities?: { name: string } | null;
  cost_categories?: { name: string } | null;
};

type Cost = Database['public']['Tables']['costs']['Row'];
type Kilometer = Database['public']['Tables']['kilometers']['Row'];
type Company = Database['public']['Tables']['companies']['Row'];

export interface CompanyData {
  projects: Project[];
  employees: Employee[];
  activities: Activity[];
  costCategories: CostCategory[];
  timeEntries: TimeEntry[];
  costs: Cost[];
  kilometers: Kilometer[];
  companies: Company[];
}

const getDateString = (dateRange: string) => {
  const now = new Date();
  let startDate = new Date();
  
  switch (dateRange) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'week':
      const day = now.getDay();
      const diff = now.getDate() - day + (day === 0 ? -6 : 1);
      startDate = new Date(now.getFullYear(), now.getMonth(), diff);
      break;
    case 'month':
      startDate = new Date(now.getFullYear(), now.getMonth(), 1);
      break;
    case 'year':
    default:
      startDate = new Date(now.getFullYear(), 0, 1);
      break;
  }
  
  startDate.setHours(0, 0, 0, 0);
  return startDate.toISOString().split('T')[0];
};

export const useCompanyData = (companyId: string | undefined, dateRange: string = 'year') => {
  return useQuery<CompanyData>({
    queryKey: ['companyData', companyId, dateRange],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();

      if (!companyId) {
        return { projects: [], employees: [], activities: [], costCategories: [], timeEntries: [], costs: [], kilometers: [], companies: [] };
      }

      const dateString = getDateString(dateRange);

      try {
        const masterDataPromise = Promise.all([
          supabase.from('projects').select('*').eq('company_id', companyId).order('name'),
          supabase.from('employees').select('id, full_name, email, role, hourly_rate, status, company_id, user_id, created_at, updated_at, companies(name)').eq('company_id', companyId).order('full_name'),
          supabase.from('activities').select('*').eq('company_id', companyId).order('name'),
          supabase.from('cost_categories').select('*').eq('company_id', companyId).order('name'),
          supabase.from('companies').select('*').eq('id', companyId),
        ]);

        const transactionalDataPromise = Promise.all([
          supabase.from('time_entries').select('*, projects(name, hourly_rate), employees(full_name, hourly_rate), activities(name), cost_categories(name)').eq('company_id', companyId).gte('date', dateString).order('date', { ascending: false }).limit(1000),
          supabase.from('costs').select('*, projects(name), cost_categories(name)').eq('company_id', companyId).gte('date', dateString).order('date', { ascending: false }).limit(1000),
          supabase.from('kilometers').select('*, projects(name)').eq('company_id', companyId).gte('date', dateString).order('date', { ascending: false }).limit(1000),
        ]);

        const [masterResults, transactionalResults] = await Promise.all([
          masterDataPromise,
          transactionalDataPromise,
        ]);

        const [projectsResult, employeesResult, activitiesResult, costCategoriesResult, companiesResult] = masterResults;
        const [timeEntriesResult, costsResult, kilometersResult] = transactionalResults;
        const allResults = [...masterResults, ...transactionalResults];

        for (const result of allResults) {
          if (result.error) {
            throw new Error(`Database query failed: ${result.error.message}`);
          }
        }

        const data: CompanyData = {
          projects: projectsResult.data || [],
          employees: employeesResult.data || [],
          activities: activitiesResult.data || [],
          costCategories: costCategoriesResult.data || [],
          timeEntries: timeEntriesResult.data || [],
          costs: costsResult.data || [],
          kilometers: kilometersResult.data || [],
          companies: companiesResult.data || [],
        };

        return data;
      } catch (error) {
        console.error('useCompanyData fetch error:', error);
        throw error;
      }
    },
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 2,
  });
};